import { faSearch, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { SxProps } from '@mui/material/styles';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListParams } from 'src/utils/useListSearchParams';

import { Icon, IconDefinition } from './Icon';
import { IconButton } from './IconButton';
import { InputAdornment, TextField } from './TextField';
import { Theme, useMediaQuery } from './muiTheme/utils';
interface SearchParamSearchTextFieldProps {
  params: ListParams;
  updateParam: (k: string, v: any) => void;
  debounceDuration?: number;
  size?: 'small' | 'medium';
  sx?: SxProps;
  placeholder?: string;
  endAdornment?: React.ReactNode;
  responsiveDefaultWidth?: boolean;
}

export const SearchParamSearchTextField: React.FC<
  SearchParamSearchTextFieldProps
> = ({
  params,
  updateParam,
  debounceDuration = 400,
  size,
  sx,
  placeholder,
  endAdornment,
  responsiveDefaultWidth,
}) => {
  const [internalValue, setInternalValue] = useState(params.search);

  const debouncedSetSearchParam = useMemo(() => {
    return debounce((newVal) => {
      updateParam('search', newVal);
    }, debounceDuration);
  }, [debounceDuration, updateParam]);

  const onChange = useCallback(
    (newValue) => {
      setInternalValue(newValue);
      debouncedSetSearchParam(newValue);
    },
    [debouncedSetSearchParam]
  );

  useEffect(() => {
    setInternalValue(params.search);
  }, [params.search]);

  const onCancel = () => {
    debouncedSetSearchParam.cancel();
    setInternalValue('');
    updateParam('search', '');
  };

  useEffect(() => {
    return () => {
      debouncedSetSearchParam.cancel();
    };
  }, [debouncedSetSearchParam]);

  return (
    <SearchTextField
      value={internalValue}
      onSearch={onChange}
      onCancel={onCancel}
      placeholder={placeholder}
      endAdornment={endAdornment}
      responsiveDefaultWidth={responsiveDefaultWidth}
      size={size}
      sx={sx}
    />
  );
};

interface SearchTextFieldProps {
  value: string;
  onCancel: () => void;
  onSearch: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  responsiveDefaultWidth?: boolean;
  size?: 'small' | 'medium';
  icon?: IconDefinition;
  showClearButton?: boolean;
  autoFocus?: boolean;
  sx?: SxProps;
  endAdornment?: React.ReactNode;
}
export const SearchTextField: React.FC<SearchTextFieldProps> = ({
  value,
  onCancel,
  onSearch,
  onFocus,
  onBlur,
  responsiveDefaultWidth,
  size,
  showClearButton,
  icon = faSearch,
  placeholder = 'Search',
  autoFocus,
  sx,
  endAdornment,
}) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <TextField
      sx={[
        { '& .MuiInputBase-root': { pr: 0 } },
        ...(responsiveDefaultWidth
          ? [{ width: smDown ? '100%' : '280px' }]
          : [{}]),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      placeholder={placeholder}
      startAdornment={
        <InputAdornment position="start">
          <Icon color="action" size="medium" icon={icon} />
        </InputAdornment>
      }
      endAdornment={
        (value || showClearButton || endAdornment) && (
          <InputAdornment position="end">
            {endAdornment}
            {(value || showClearButton) && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onCancel();
                }}
              >
                <Icon color="secondary" size="medium" icon={faTimesCircle} />
              </IconButton>
            )}
          </InputAdornment>
        )
      }
      value={value}
      onChange={(event) => onSearch(event.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      size={size}
    />
  );
};
